import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, Badge, cn } from '@tractian/ui'

import type {
  HubspotSearchItem,
  HubspotSearchOwner
} from '@/features/assessments/api/hubspot-search'

export type HubspotSearchOptionProps = ComponentPropsWithoutRef<'div'> & {
  item: HubspotSearchItem
  // Owned by the parent list (keyboard highlight, current value).
  highlighted?: boolean
  selected?: boolean
}

function formatShortDate(value: string, locale: string): string | null {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date)
}

function OwnerLabel({ owner }: { owner: HubspotSearchOwner }) {
  return (
    <span className="flex min-w-0 items-center gap-1">
      {/* Same fallback as the assessments table: the owner's initial. */}
      <Avatar size="small" className="shrink-0">
        {owner.imageUrl && <Avatar.Image src={owner.imageUrl} alt="" />}
        <Avatar.Fallback data-testid="hubspot-search-option-avatar-placeholder">
          {owner.name.charAt(0).toUpperCase()}
        </Avatar.Fallback>
      </Avatar>
      <span className="truncate">{owner.name}</span>
    </span>
  )
}

export function HubspotSearchOption({
  item,
  highlighted = false,
  selected = false,
  className,
  ...props
}: HubspotSearchOptionProps) {
  const { t, i18n } = useTranslation('plantAssessment')

  const lastActivity =
    item.type === 'deal' && item.lastActivityAt
      ? formatShortDate(item.lastActivityAt, i18n.language)
      : null
  const details: { key: string; node: ReactNode }[] = []
  if (item.type === 'deal') {
    if (item.dealOwner) {
      details.push({ key: 'deal-owner', node: <OwnerLabel owner={item.dealOwner} /> })
    }
    if (item.salesEngineerOwner) {
      details.push({ key: 'se-owner', node: <OwnerLabel owner={item.salesEngineerOwner} /> })
    }
    if (lastActivity) {
      details.push({
        key: 'last-activity',
        node: (
          <span className="shrink-0">
            {t('hubspotSearchOption.lastActivity', { date: lastActivity })}
          </span>
        )
      })
    }
  } else if (item.address) {
    details.push({ key: 'address', node: <span className="truncate">{item.address}</span> })
  }

  return (
    <div
      data-highlighted={highlighted || undefined}
      data-selected={selected || undefined}
      className={cn(
        'flex min-w-0 flex-col gap-0.5 rounded px-2 py-1.5',
        highlighted && 'bg-slate-100',
        selected && 'bg-blue-50',
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Badge size="small" shape="square" className="shrink-0">
          {t(`hubspotSearchOption.type.${item.type}`)}
        </Badge>
        <span className="truncate text-sm font-medium text-slate-900" title={item.title}>
          {item.title}
        </span>
      </div>
      {details.length > 0 && (
        <div
          data-testid="hubspot-search-option-details"
          className="flex min-w-0 items-center gap-1 text-xs text-slate-500"
        >
          {details.map((detail, index) => (
            <span key={detail.key} className="flex min-w-0 items-center">
              {detail.node}
              {index < details.length - 1 && <span aria-hidden="true">,</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
